// Third Party
import moment from "moment/moment";

// Utils
import { and } from "@Common/helpers/sonar";
import { isPreOwned } from "@DataPages/components/SalesOperations/Tabs/SalesLog/SalesLogAnalyticsSection";
// Types
import { ICommissionCycle } from "@/Common/api/types/CommissionCycle";
import { IAnalytics, IDynamicPVRInfo, ISalesAnalyticsItem, ISalesLog } from "@DataPages/types/salesOperations/salesLog/model";
import { MixedType, TDataProperty, TDealTypeKey, TIncludedState } from "@DataPages/types/salesOperations/salesLog/utils";

// Constants
import { TAB_NAMES } from "@/DataPages/constants/sales-operations";

// Types
type TransformedDataProps = {
	data: ISalesAnalyticsItem[];
	daysInCurrentMonth: number;
	dataProperty: TDataProperty;
	chartIndex: number;
	tabSelected: string;
	selected: ICommissionCycle;
	lastActiveCommissionCycle: ICommissionCycle;
	isLastDayOfMonth: boolean;
};

type AccumulatedValueProps = {
	idx: number;
	data: ISalesAnalyticsItem[];
	dataProperty: TDataProperty;
	chartIndex: number;
	tabSelected: string;
};

type GetValueFromDataProps = {
	idx: number;
	data: ISalesAnalyticsItem[];
	dataProperty: TDataProperty;
	chartIndex: number;
	tabSelected: string;
};
type PossibleProps = "totalGross" | "backGross" | "frontGross";

export function getProjectedProperty(tabName: string): PossibleProps {
	switch (tabName) {
		case TAB_NAMES.COMBINED:
			return "totalGross";
		case TAB_NAMES.FI:
			return "backGross";
		default:
			return "frontGross";
	}
}

export function getValueFromData({ idx, data, dataProperty, chartIndex, tabSelected }: GetValueFromDataProps) {
	const property = dataProperty === "deliveredToday" ? "summary" : "daily";
	let subProperty: string;
	if (chartIndex === 0) {
		subProperty = getProjectedProperty(tabSelected);
	} else {
		subProperty = "deliveredToday" as const;
	}
	const value = data?.[idx]?.[property];
	if (!value) {
		return 0;
	}

	if ("deliveredToday" in value) {
		return value?.[subProperty as "deliveredToday"];
	} else {
		return value?.[subProperty as PossibleProps];
	}
}

export function accumulatedValue({ idx, data, dataProperty, chartIndex, tabSelected }: AccumulatedValueProps) {
	let totalSum = 0;
	for (let i = 0; i < idx; i++) {
		totalSum += Number(
			getValueFromData({
				idx: i,
				data,
				dataProperty,
				chartIndex,
				tabSelected,
			}) || 0
		);
	}
	return totalSum;
}

function getFirstDayData({
	idx,
	data,
	dataProperty,
	chartIndex,
	tabSelected,
}: {
	idx: number;
	data: ISalesAnalyticsItem[];
	dataProperty: TDataProperty;
	chartIndex: number;
	tabSelected: string;
}) {
	return {
		...data[idx],
		deliveredToday: [
			0,
			Number(
				getValueFromData({
					idx,
					data,
					dataProperty,
					chartIndex,
					tabSelected,
				})
			),
		],
		frontGross: [
			0,
			Number(
				getValueFromData({
					idx,
					data,
					dataProperty,
					chartIndex,
					tabSelected,
				})
			),
		],
		day: idx + 1,
	};
}

function getLastDayData({ idx, data, tabSelected }: { idx: number; data: ISalesAnalyticsItem[]; tabSelected: string }) {
	return {
		...data[idx],
		day: idx + 1,
		frontGross: [0, Number(data?.[moment().date() - 1]?.projection?.projected?.[getProjectedProperty(tabSelected)])],
		deliveredToday: [0, Number(data?.[moment().date() - 1]?.summary?.salesPace)],
	};
}

function getOtherDayData({
	idx,
	data,
	tabSelected,
	dataProperty,
	chartIndex,
}: {
	idx: number;
	data: ISalesAnalyticsItem[];
	tabSelected: string;
	dataProperty: TDataProperty;
	chartIndex: number;
}) {
	const deliveredToday = Number(data?.[idx]?.summary?.deliveredToday);
	const value = Number(data?.[idx]?.daily?.[getProjectedProperty(tabSelected)]);
	const accumulated = accumulatedValue({ idx, data, dataProperty, chartIndex, tabSelected });

	return {
		...data[idx],
		day: idx + 1,
		deliveredToday: [accumulated, accumulated + deliveredToday],
		frontGross: [accumulated, accumulated + value],
	};
}

export function roundUpToNearest500(number = 0) {
	const multiplier = 500;
	if (number >= 0) {
		return Math.ceil(number / multiplier) * multiplier;
	} else {
		return Math.floor(number / multiplier) * multiplier;
	}
}

export function getTransformedData({
	data,
	daysInCurrentMonth,
	dataProperty,
	chartIndex,
	tabSelected,
	selected,
	lastActiveCommissionCycle,
	isLastDayOfMonth,
}: TransformedDataProps): MixedType[] {
	return Array.from({ length: daysInCurrentMonth }, (_, idx) => {
		// First day of the month
		if (idx === 0) {
			return getFirstDayData({ idx, data, dataProperty, chartIndex, tabSelected });
		} else if (
			idx === daysInCurrentMonth - 1 &&
			moment(selected?.startDate)
				.utc()
				.isBetween(lastActiveCommissionCycle?.startDate, lastActiveCommissionCycle?.endDate, null, "[]") &&
			!isLastDayOfMonth &&
			!moment(selected?.endDate).isBefore(moment())
		) {
			return getLastDayData({ idx, data, tabSelected });
		} else if (data[idx]) {
			return getOtherDayData({ idx, data, tabSelected, dataProperty, chartIndex });
		} else {
			return {
				day: idx + 1,
			};
		}
	}) as unknown as MixedType[];
}

export function getAccumulativeValue({
	chartIndex,
	transformedData,
	activeDay,
	tabSelected,
}: {
	chartIndex: number;
	transformedData: ISalesAnalyticsItem[];
	activeDay: number;
	tabSelected: string;
}) {
	let accumulativeValue = 0;
	if (chartIndex === 0) {
		accumulativeValue = transformedData[activeDay - 1]?.["projection"]?.["current"]?.[getProjectedProperty(tabSelected)];
	} else if (chartIndex === 1) {
		accumulativeValue = transformedData[activeDay - 1]?.["summary"]?.["totalDelivered"];
	}
	return accumulativeValue ?? 0;
}

const getTabSales = (tab: string, sales: ISalesLog[], includedDealType?: TIncludedState): ISalesLog[] => {
	if (!sales?.length) {
		return [];
	}

	if (!tab) {
		return sales;
	}

	const filteredDealTypes: Array<TDealTypeKey> = Object.entries(includedDealType || {})
		.filter(([_, value]) => value.included)
		.map(([key]) => key as TDealTypeKey);

	const SALES_MAP: Record<string, (sales: ISalesLog[], filteredDealTypes?: Array<TDealTypeKey>) => ISalesLog[]> = {
		[TAB_NAMES.COMBINED]: getFilteredSalesLogs,
		[TAB_NAMES.FI]: getFilteredSalesLogs,
		[TAB_NAMES.NEW_VEHICLES]: getNewSalesLogs,
		[TAB_NAMES.PRE_OWNED_VEHICLES]: getPreOwnedSalesLogs,
		[TAB_NAMES.WHOLESALE]: getFilteredSalesLogs,
	};

	const salesMapFn = SALES_MAP[tab];

	return salesMapFn ? salesMapFn(sales, filteredDealTypes) : getSalesByFranchiseName(sales, tab, filteredDealTypes);
};

function getSalesByFranchiseName(sales: ISalesLog[], franchiseName: string, filteredDealTypes?: Array<TDealTypeKey>): ISalesLog[] {
	return sales?.filter(
		(sale) => !isPreOwned(sale.saleType) && sale.franchiseName === franchiseName && filteredDealTypes?.includes(sale.dealType)
	);
}

function getFilteredSalesLogs(sales: ISalesLog[], filteredDealTypes?: Array<TDealTypeKey>): ISalesLog[] {
	if (!filteredDealTypes?.length) {
		return [];
	}

	return sales?.filter((sale) => filteredDealTypes?.includes(sale.dealType));
}

function getNewSalesLogs(sales: ISalesLog[], filteredDealTypes?: Array<TDealTypeKey>) {
	if (!filteredDealTypes?.length) {
		return [];
	}

	return sales?.filter((sale) => !isPreOwned(sale.saleType) && filteredDealTypes?.includes(sale.dealType));
}

function getPreOwnedSalesLogs(sales: ISalesLog[], filteredDealTypes?: Array<TDealTypeKey>) {
	if (!filteredDealTypes?.length) {
		return [];
	}

	return sales?.filter((sale) => isPreOwned(sale.saleType) && filteredDealTypes?.includes(sale.dealType));
}

const getTabDynamicPVRInfo = (tab: string, dynamicPVRInfo: IDynamicPVRInfo[]): IDynamicPVRInfo | undefined => {
	switch (tab) {
		case TAB_NAMES.FI:
			return dynamicPVRInfo.find((pvrInfo) => pvrInfo.analyticReportName === "F&I");
		case TAB_NAMES.COMBINED:
			return dynamicPVRInfo?.find((pvrInfo) => pvrInfo.analyticReportName === "Total");
		case TAB_NAMES.NEW_VEHICLES:
			return dynamicPVRInfo?.find((pvrInfo) =>
				and(
					pvrInfo.analyticReportName !== "F&I",
					pvrInfo.analyticReportName !== "Total",
					pvrInfo.analyticReportName !== "Pre-owned",
					pvrInfo.analyticReportName !== TAB_NAMES.WHOLESALE
				)
			);
		case TAB_NAMES.PRE_OWNED_VEHICLES:
			return dynamicPVRInfo?.find((pvrInfo) => pvrInfo.analyticReportName === "Pre-owned");
		case TAB_NAMES.WHOLESALE:
			return dynamicPVRInfo?.find((pvrInfo) => pvrInfo.analyticReportName === TAB_NAMES.WHOLESALE);
		default:
			return dynamicPVRInfo?.find((pvrInfo) => pvrInfo.analyticReportName === tab);
	}
};

const getTabAnalytics = (tab: string, analytics: IAnalytics): ISalesAnalyticsItem[] => {
	switch (tab) {
		case TAB_NAMES.COMBINED:
		case TAB_NAMES.FI:
			return analytics?.totals?.salesMetrics ?? ([] as ISalesAnalyticsItem[]);
		case TAB_NAMES.PRE_OWNED_VEHICLES:
			return analytics?.preOwned?.salesMetrics ?? ([] as ISalesAnalyticsItem[]);
		case TAB_NAMES.WHOLESALE:
			return analytics?.wholesale?.salesMetrics ?? ([] as ISalesAnalyticsItem[]);
		case TAB_NAMES.NEW_VEHICLES:
			return analytics?.new?.[0]?.salesMetrics ?? ([] as ISalesAnalyticsItem[]);
		default:
			return analytics?.new?.find((analytic) => analytic.franchiseName === tab)?.salesMetrics ?? ([] as ISalesAnalyticsItem[]);
	}
};

const getMetricToCalculate = (analyticTab: string) => {
	switch (analyticTab) {
		case TAB_NAMES.FI:
			return "backGross";
		case TAB_NAMES.COMBINED:
			return "totalGross";
		default:
			return "frontGross";
	}
};

const calculateDynamicPVRInfo = (tabAnalytics: ISalesAnalyticsItem[], tabName: string) => {
	const pvrInfo = {
		analyticReportName: "",
		minValue: 0,
		maxValue: 0,
		average: 0,
	};

	let rawPVRAnalytics = tabAnalytics.map((dailyStatistic) => {
		const dividend = Number(dailyStatistic.daily?.[getMetricToCalculate(tabName)]);
		const divisor = Number(dailyStatistic.summary?.deliveredToday);

		if (divisor === 0) {
			return 0;
		}
		return Number(dividend) / Number(divisor);
	});

	// Sort the analytic for the given report tab to get the info we need about the analytics of that report.
	rawPVRAnalytics = rawPVRAnalytics.sort((a, b) => a - b);

	const minValue = rawPVRAnalytics[0];
	const maxValue = rawPVRAnalytics[rawPVRAnalytics.length - 1];
	const averageValue = (maxValue + minValue) / 2;

	pvrInfo.analyticReportName = tabName;
	pvrInfo.minValue = minValue;
	pvrInfo.maxValue = maxValue;
	pvrInfo.average = averageValue;

	return pvrInfo;
};

export { getTabSales, getTabAnalytics, getTabDynamicPVRInfo, calculateDynamicPVRInfo };
